import React, { useState, useEffect } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  POST_LENGTHS,
  WORDPRESS_POST_LENGTHS,
} from "./constants/postSettings";
import { getLanguageName } from "./constants/languages";
import { adminFetch } from "./utils/adminApi";

const WEEKDAY_LABELS = {
  monday: "Thứ 2",
  tuesday: "Thứ 3",
  wednesday: "Thứ 4",
  thursday: "Thứ 5",
  friday: "Thứ 6",
  saturday: "Thứ 7",
  sunday: "Chủ nhật",
};

const SOURCE_META = {
  posts: {
    label: "Auto viết bài",
    className: "bg-indigo-100 text-indigo-800",
  },
  productImages: {
    label: "Danh sách ảnh/video",
    className: "bg-pink-100 text-pink-800",
  },
};

const SENSITIVE_KEY_PATTERN =
  /(accessToken|access_token|token|password|secret|credential|apiKey|api_key)/i;

const AdminAutoPostSettings = ({ userId, onBack }) => {
  const [autoPostSettings, setAutoPostSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userName, setUserName] = useState("");
  const [userSettings, setUserSettings] = useState(null);
  const [processingConfigId, setProcessingConfigId] = useState(null);
  const [selectedSetting, setSelectedSetting] = useState(null);
  const [showSettingDetail, setShowSettingDetail] = useState(false);

  const isAutoPostEnabled = (setting) => setting?.autoPostEnabled !== false;

  useEffect(() => {
    let cancelled = false;

    const fetchSettings = async () => {
      setLoading(true);
      try {
        const result = await adminFetch(
          `/api/admin/auto-settings/${encodeURIComponent(userId)}`
        );
        if (cancelled) {
          return;
        }

        setUserName(result.data?.userName || userId);
        setUserSettings(result.data?.userSettings || null);
        setAutoPostSettings(result.data?.settings || []);
      } catch (error) {
        console.error("Lỗi khi lấy cấu hình tự động:", error);
        setUserName(userId);
        setUserSettings(null);
        setAutoPostSettings([]);
        toast.error("Không thể tải cấu hình tự động của người dùng.");
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchSettings();

    return () => {
      cancelled = true;
    };
  }, [userId]);

  const handleToggleStatus = async (setting) => {
    const nextStatus = !isAutoPostEnabled(setting);
    setProcessingConfigId(setting.id);

    try {
      const result = await adminFetch(
        `/api/admin/auto-settings/${encodeURIComponent(userId)}/${encodeURIComponent(
          setting.sourceType || "posts"
        )}/${encodeURIComponent(setting.id)}`,
        {
          method: "PATCH",
          body: JSON.stringify({ autoPostEnabled: nextStatus }),
        }
      );

      const updatedSetting = {
        ...setting,
        ...(result.data || {}),
        autoPostEnabled: nextStatus,
        updatedAt: result.data?.updatedAt || Date.now(),
      };
      setAutoPostSettings((prev) =>
        prev.map((item) =>
          item.id === setting.id && item.sourceType === setting.sourceType
            ? updatedSetting
            : item
        )
      );
      setSelectedSetting((prev) =>
        prev?.id === setting.id && prev?.sourceType === setting.sourceType
          ? updatedSetting
          : prev
      );

      toast.success(
        nextStatus
          ? "Đã bật thiết lập tự động đăng bài."
          : "Đã tắt thiết lập tự động đăng bài."
      );
    } catch (error) {
      console.error("Lỗi khi cập nhật trạng thái thiết lập:", error);
      toast.error("Không thể cập nhật trạng thái. Vui lòng thử lại.");
    } finally {
      setProcessingConfigId(null);
    }
  };

  const getPostLengthLabel = (postLength) => {
    const foundLength = [...POST_LENGTHS, ...WORDPRESS_POST_LENGTHS].find(
      (length) => length.value === postLength
    );
    return foundLength ? foundLength.label : postLength || "Chưa thiết lập";
  };

  const renderPlatformIcon = (platform) => {
    if (platform === "wordpress") {
      return (
        <span className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
          WordPress
        </span>
      );
    }

    if (platform === "zalo") {
      return (
        <span className="inline-flex items-center px-2 py-1 bg-cyan-100 text-cyan-800 text-xs rounded-full">
          Zalo OA
        </span>
      );
    }

    return (
      <span className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
        Facebook
      </span>
    );
  };

  const getSettingTitle = (setting) => {
    if (setting?.sourceType === "productImages" && setting?.name) {
      return setting.name;
    }

    if (typeof setting?.prompt === "string" && setting.prompt.trim()) {
      return setting.prompt.trim();
    }

    const products = getProducts(setting);
    const productNames = products
      .map((product) => product?.name)
      .filter((name) => typeof name === "string" && name.trim())
      .map((name) => name.trim());

    if (productNames.length === 1) return productNames[0];
    if (productNames.length > 1) return `${productNames.length} sản phẩm`;

    return setting?.sourceType === "productImages"
      ? "Thiết lập danh sách ảnh/video"
      : "Không có tiêu đề";
  };

  const formatTimestamp = (value) => {
    if (!value && value !== 0) return "Không có";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString("vi-VN", { timeZone: "Asia/Ho_Chi_Minh" });
  };

  const getReadableValue = (value) => {
    if (value === undefined || value === null || value === "") return "Không có";
    if (Array.isArray(value)) {
      if (value.length === 0) return "[]";
      if (value.every((item) => item === null || typeof item !== "object")) {
        return value.join(" | ");
      }
      return JSON.stringify(value, null, 2);
    }
    if (typeof value === "object") {
      return JSON.stringify(value, null, 2);
    }
    return String(value);
  };

  const getBooleanLabel = (value) => (value ? "Bật" : "Tắt");

  const getInputTypeLabel = (inputType) => {
    if (inputType === "topic") return "Chủ đề";
    if (inputType === "products") return "Sản phẩm";
    if (inputType === "photoList") return "Danh sách ảnh/video";
    return inputType || "Chưa thiết lập";
  };

  const getPostModeLabel = (mode) => {
    if (mode === "review") return "Duyệt trước khi đăng";
    if (mode === "direct") return "Đăng trực tiếp";
    return mode || "Chưa thiết lập";
  };

  const getScheduleLabel = (setting) => {
    if (setting?.scheduleMode === "fixed-dates") {
      if (
        Array.isArray(setting?.fixedDateSchedules) &&
        setting.fixedDateSchedules.length > 0
      ) {
        return `${setting.fixedDateSchedules.length} lịch cố định`;
      }
      return "Lịch ngày cố định";
    }

    const times = getTimes(setting);
    if (times.length > 0) return times.join(" | ");

    return "Chưa đặt giờ";
  };

  const getScheduleModeLabel = (setting) => {
    if (setting?.scheduleMode === "fixed-dates") return "Ngày cố định";
    if (setting?.scheduleMode === "weekly" || setting?.selectedDays) {
      return "Lặp lại hàng tuần";
    }
    return "Chưa thiết lập";
  };

  const getTimes = (setting) => {
    if (Array.isArray(setting?.times) && setting.times.length > 0) {
      return setting.times;
    }

    if (Array.isArray(setting?.postTimes) && setting.postTimes.length > 0) {
      return setting.postTimes;
    }

    if (setting?.time) return [setting.time];
    if (setting?.postTime) return [setting.postTime];
    return [];
  };

  const getSelectedDayLabels = (setting) => {
    const selectedDays = setting?.selectedDays;
    if (!selectedDays || typeof selectedDays !== "object") return [];

    return Object.entries(WEEKDAY_LABELS)
      .filter(([key]) => selectedDays[key])
      .map(([, label]) => label);
  };

  const getFanpages = (setting) => {
    if (Array.isArray(setting?.selectedFanpages) && setting.selectedFanpages.length) {
      return setting.selectedFanpages;
    }

    if (Array.isArray(setting?.fanpages) && setting.fanpages.length) {
      return setting.fanpages;
    }

    if (setting?.fanpageID || setting?.fanpageName) {
      return [
        {
          id: setting.fanpageID || "",
          name: setting.fanpageName || "Fanpage chưa đặt tên",
          access_token: setting.accessToken || "",
        },
      ];
    }

    return [];
  };

  const getProducts = (setting) => {
    if (Array.isArray(setting?.products)) return setting.products.filter(Boolean);
    if (Array.isArray(setting?.fanpageProducts)) {
      return setting.fanpageProducts.filter(Boolean);
    }

    if (setting?.products && typeof setting.products === "object") {
      return Object.values(setting.products).filter(Boolean);
    }

    if (typeof setting?.products === "string" && setting.products.trim()) {
      return [{ name: setting.products.trim() }];
    }

    if (setting?.sourceType === "productImages" && setting?.name) {
      return [
        {
          name: setting.name,
          description: setting.info || "",
          customInfo: setting.customInfo || "",
        },
      ];
    }

    return [];
  };

  const normalizeTopicList = (setting) => {
    if (!setting?.topics) return [];

    const toTopic = (topic, index) => {
      if (typeof topic === "string") {
        return {
          id: `topic-${index + 1}`,
          title: topic,
          postStatus: "not_started",
        };
      }

      return {
        id: topic?.id || `topic-${index + 1}`,
        title: topic?.title || topic?.topic || `Chủ đề ${index + 1}`,
        postStatus: topic?.postStatus || topic?.status || "not_started",
        generatedPost: topic?.generatedPost || "",
        errorMessage: topic?.errorMessage || topic?.error || "",
      };
    };

    if (Array.isArray(setting.topics)) {
      return setting.topics.map(toTopic).filter((topic) => topic.title);
    }

    if (typeof setting.topics === "object") {
      return Object.values(setting.topics)
        .map(toTopic)
        .filter((topic) => topic.title);
    }

    return [];
  };

  const getMediaItems = (setting, collectionName) => {
    const mediaCollection = setting?.[collectionName];
    if (!mediaCollection || typeof mediaCollection !== "object") return [];

    return Object.entries(mediaCollection)
      .map(([key, value]) => ({
        id: value?.id || key,
        type: collectionName === "videos" ? "video" : "image",
        ...value,
      }))
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  };

  const getTopicStatusMeta = (statusRaw) => {
    const status = String(statusRaw || "not_started").toLowerCase();
    if (status === "posted") {
      return { label: "Đã đăng", className: "bg-emerald-100 text-emerald-700" };
    }
    if (status === "failed") {
      return { label: "Thất bại", className: "bg-red-100 text-red-700" };
    }
    if (status === "in_progress") {
      return { label: "Đang xử lý", className: "bg-blue-100 text-blue-700" };
    }
    if (status === "pending_review") {
      return { label: "Chờ duyệt", className: "bg-amber-100 text-amber-700" };
    }
    return { label: "Chưa đăng", className: "bg-gray-100 text-gray-700" };
  };

  const sanitizeForDisplay = (value) => {
    if (Array.isArray(value)) return value.map(sanitizeForDisplay);
    if (!value || typeof value !== "object") return value;

    return Object.entries(value).reduce((acc, [key, itemValue]) => {
      if (SENSITIVE_KEY_PATTERN.test(key)) {
        acc[key] = itemValue ? "[Đã ẩn]" : itemValue;
      } else {
        acc[key] = sanitizeForDisplay(itemValue);
      }
      return acc;
    }, {});
  };

  const renderValueRow = (label, value, options = {}) => {
    if (
      options.hideWhenEmpty &&
      (value === undefined || value === null || value === "")
    ) {
      return null;
    }

    return (
      <div className="text-sm text-gray-700">
        <span className="font-medium">{label}: </span>
        <span className={options.breakWords ? "break-words" : ""}>
          {value === undefined || value === null || value === ""
            ? "Không có"
            : value}
        </span>
      </div>
    );
  };

  const renderPreBlock = (title, value) => (
    <div className="bg-white border rounded-lg p-4">
      <h4 className="font-semibold text-gray-800 mb-2">{title}</h4>
      <pre className="bg-gray-50 rounded p-3 overflow-x-auto whitespace-pre-wrap text-sm">
        {getReadableValue(value)}
      </pre>
    </div>
  );

  const renderSection = (title, children) => (
    <div className="bg-gray-50 rounded-lg p-4 space-y-3">
      <h4 className="font-semibold text-gray-800">{title}</h4>
      {children}
    </div>
  );

  const renderFanpageList = (setting) => {
    const fanpages = getFanpages(setting);

    if (fanpages.length === 0) {
      return (
        <div className="bg-white rounded p-3 border text-sm text-gray-600">
          Chưa chọn fanpage.
        </div>
      );
    }

    return (
      <div className="space-y-2">
        {fanpages.map((fanpage, index) => (
          <div
            key={`${fanpage.id || fanpage.name || "fanpage"}-${index}`}
            className="bg-white rounded border p-3 text-sm"
          >
            <div className="font-medium text-gray-800">
              {index + 1}. {fanpage.name || "Fanpage chưa đặt tên"}
            </div>
            <div className="mt-1 text-xs text-gray-600">
              ID: {fanpage.id || "Không có"}
            </div>
            <div className="text-xs text-gray-600">
              Token: {fanpage.access_token || fanpage.accessToken ? "Có" : "Không có"}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderProductList = (setting) => {
    const products = getProducts(setting);

    if (products.length === 0) {
      return (
        <div className="bg-white rounded p-3 border text-sm text-gray-600">
          Không có dữ liệu sản phẩm trong thiết lập này.
        </div>
      );
    }

    return (
      <div className="space-y-2 max-h-96 overflow-auto">
        {products.map((product, index) => {
          const trackingKey =
            product?.trackingKey ||
            (product?.id !== undefined && product?.id !== null
              ? `product_id_${product.id}`
              : "");
          const productState =
            trackingKey && setting?.productPostingState
              ? setting.productPostingState[trackingKey]
              : null;

          return (
            <div
              key={`${product?.id || product?.name || "product"}-${index}`}
              className="bg-white rounded border p-3 text-sm"
            >
              <div className="font-medium text-gray-800">
                {index + 1}. {product?.name || "Sản phẩm chưa đặt tên"}
              </div>
              {product?.price !== undefined && product?.price !== "" && (
                <div className="mt-1 text-xs text-gray-600">
                  Giá: {product.price}
                </div>
              )}
              {(product?.description || product?.info) && (
                <div className="mt-1 text-xs text-gray-600 whitespace-pre-wrap">
                  {product.description || product.info}
                </div>
              )}
              {product?.customInfo && (
                <div className="mt-1 text-xs text-gray-600 whitespace-pre-wrap">
                  Thông tin bổ sung: {product.customInfo}
                </div>
              )}
              {product?.permalink && (
                <a
                  href={product.permalink}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-1 inline-block text-xs text-blue-600 hover:underline break-all"
                >
                  {product.permalink}
                </a>
              )}
              {productState && (
                <pre className="mt-2 bg-gray-50 rounded p-2 border text-xs overflow-x-auto whitespace-pre-wrap">
                  {JSON.stringify(productState, null, 2)}
                </pre>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderMediaList = (setting) => {
    const images = getMediaItems(setting, "images");
    const videos = getMediaItems(setting, "videos");
    const mediaItems = [...images, ...videos].sort(
      (a, b) => (a.position ?? 0) - (b.position ?? 0)
    );

    if (mediaItems.length === 0) {
      return (
        <div className="bg-white rounded p-3 border text-sm text-gray-600">
          Không có ảnh/video trong thiết lập này.
        </div>
      );
    }

    const postedCount = mediaItems.filter((item) => item.postStatus === true).length;

    return (
      <div className="space-y-3">
        <div className="flex flex-wrap gap-2">
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
            Tổng media: {mediaItems.length}
          </span>
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
            Đã đăng: {postedCount}
          </span>
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
            Chưa đăng: {mediaItems.length - postedCount}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-96 overflow-auto">
          {mediaItems.map((item, index) => (
            <div
              key={`${item.type}-${item.id || index}`}
              className="bg-white rounded border p-3 text-sm"
            >
              <div className="flex gap-3">
                {item.type === "image" && item.url ? (
                  <img
                    src={item.url}
                    alt={item.id || `image-${index + 1}`}
                    className="w-20 h-20 rounded object-cover border bg-gray-50"
                  />
                ) : (
                  <div className="w-20 h-20 rounded border bg-gray-100 flex items-center justify-center text-xs text-gray-500">
                    Video
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-gray-800">
                    {index + 1}. {item.type === "video" ? "Video" : "Ảnh"}
                  </div>
                  <div className="text-xs text-gray-600">
                    ID: {item.id || "Không có"}
                  </div>
                  <div className="text-xs text-gray-600">
                    Vị trí: {item.position ?? index}
                  </div>
                  <div className="text-xs text-gray-600">
                    Trạng thái: {item.postStatus ? "Đã đăng" : "Chưa đăng"}
                  </div>
                  {item.url && (
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-blue-600 hover:underline break-all"
                    >
                      Mở media
                    </a>
                  )}
                </div>
              </div>
              {item.firstCommentText && (
                <div className="mt-2 text-xs text-gray-700 bg-gray-50 border rounded p-2 whitespace-pre-wrap">
                  Comment đầu tiên: {item.firstCommentText}
                </div>
              )}
              {item.postError && (
                <div className="mt-2 text-xs text-red-600 bg-red-50 border border-red-100 rounded p-2">
                  Lỗi: {item.postError}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const openSettingDetail = (setting) => {
    setSelectedSetting(setting);
    setShowSettingDetail(true);
  };

  const closeSettingDetail = () => {
    setSelectedSetting(null);
    setShowSettingDetail(false);
  };

  const selectedTopicList = normalizeTopicList(selectedSetting);
  const postedTopics = selectedTopicList.filter(
    (topic) => String(topic.postStatus || "").toLowerCase() === "posted"
  ).length;
  const failedTopics = selectedTopicList.filter(
    (topic) => String(topic.postStatus || "").toLowerCase() === "failed"
  ).length;
  const pendingTopics = selectedTopicList.length - postedTopics - failedTopics;
  const sourceMeta =
    SOURCE_META[selectedSetting?.sourceType] || SOURCE_META.posts;
  const selectedFanpageCount = getFanpages(selectedSetting).length;
  const selectedProductsCount = getProducts(selectedSetting).length;
  const selectedImagesCount = getMediaItems(selectedSetting, "images").length;
  const selectedVideosCount = getMediaItems(selectedSetting, "videos").length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 admin-auto-post-settings">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
        <div className="flex items-center">
          <button
            onClick={onBack}
            className="mr-4 bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold py-2 px-4 rounded inline-flex items-center"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
            </svg>
            Quay lại
          </button>
          <div>
            <h2 className="text-xl font-semibold text-gray-800">
              Thiết lập tự động đăng bài của {userName}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Bao gồm cấu hình từ AutoPost, Product Auto Post và danh sách
              ảnh/video.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-800">
            Auto viết bài:{" "}
            {autoPostSettings.filter((item) => item.sourceType === "posts").length}
          </span>
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-pink-100 text-pink-800">
            DS ảnh/video:{" "}
            {
              autoPostSettings.filter(
                (item) => item.sourceType === "productImages"
              ).length
            }
          </span>
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
            Đang chạy: {autoPostSettings.filter(isAutoPostEnabled).length}
          </span>
        </div>
      </div>

      {userSettings && Object.keys(userSettings).length > 0 && (
        <details className="mb-6 bg-white rounded-lg shadow border">
          <summary className="px-4 py-3 cursor-pointer font-semibold text-gray-800 select-none">
            Thiết lập mặc định lưu trong hồ sơ người dùng
          </summary>
          <div className="px-4 pb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              {renderValueRow(
                "Ngôn ngữ mặc định",
                userSettings.selectedLanguage
                  ? getLanguageName(userSettings.selectedLanguage)
                  : "Không có"
              )}
              {renderValueRow("Vị trí logo", userSettings.logoPosition)}
              {renderValueRow(
                "Độ trong suốt logo",
                userSettings.logoOpacity !== undefined
                  ? `${Math.round(Number(userSettings.logoOpacity) * 100)}%`
                  : "Không có"
              )}
              {renderValueRow(
                "Kích thước logo",
                userSettings.logoSize ? `${userSettings.logoSize}%` : "Không có"
              )}
            </div>
            <pre className="bg-slate-900 text-slate-100 rounded-lg p-4 overflow-auto max-h-72 text-xs">
              {JSON.stringify(sanitizeForDisplay(userSettings), null, 2)}
            </pre>
          </div>
        </details>
      )}

      {autoPostSettings.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-6 text-center">
          <p className="text-gray-600">
            Người dùng này chưa có thiết lập tự động đăng bài nào.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {autoPostSettings.map((setting) => {
            const itemSourceMeta =
              SOURCE_META[setting.sourceType] || SOURCE_META.posts;
            const fanpageCount = getFanpages(setting).length;
            const productCount = getProducts(setting).length;
            const mediaCount =
              getMediaItems(setting, "images").length +
              getMediaItems(setting, "videos").length;

            return (
              <div
                key={`${setting.sourceType}-${setting.id}`}
                className="bg-white rounded-lg shadow overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => openSettingDetail(setting)}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    openSettingDetail(setting);
                  }
                }}
              >
                <div className="p-4 border-b">
                  <div className="flex justify-between items-start gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap gap-2 mb-2">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${itemSourceMeta.className}`}
                        >
                          {setting.sourceTitle || itemSourceMeta.label}
                        </span>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            isAutoPostEnabled(setting)
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {isAutoPostEnabled(setting) ? "Đang chạy" : "Đã tắt"}
                        </span>
                      </div>
                      <h3 className="font-bold text-gray-800 line-clamp-2">
                        {getSettingTitle(setting)}
                      </h3>
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  <div className="space-y-2">
                    <div className="flex items-center text-gray-600 text-sm">
                      <span className="font-medium mr-2">Nền tảng:</span>
                      {renderPlatformIcon(setting.platform)}
                    </div>
                    <div className="text-gray-600 text-sm">
                      <span className="font-medium mr-2">Lịch:</span>
                      <span>{getScheduleLabel(setting)}</span>
                    </div>
                    <div className="text-gray-600 text-sm">
                      <span className="font-medium mr-2">Độ dài:</span>
                      <span>{getPostLengthLabel(setting.postLength)}</span>
                    </div>
                    <div className="text-gray-600 text-sm">
                      <span className="font-medium mr-2">Phong cách:</span>
                      <span>{setting.postStyles || "Chưa thiết lập"}</span>
                    </div>
                    <div className="text-gray-600 text-sm">
                      <span className="font-medium mr-2">Loại đầu vào:</span>
                      <span>{getInputTypeLabel(setting.inputType)}</span>
                    </div>
                    <div className="flex flex-wrap gap-2 pt-1">
                      <span className="px-2 py-1 rounded bg-gray-100 text-gray-700 text-xs">
                        Fanpage: {fanpageCount}
                      </span>
                      <span className="px-2 py-1 rounded bg-gray-100 text-gray-700 text-xs">
                        Sản phẩm: {productCount}
                      </span>
                      {setting.sourceType === "productImages" && (
                        <span className="px-2 py-1 rounded bg-gray-100 text-gray-700 text-xs">
                          Media: {mediaCount}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t">
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleToggleStatus(setting);
                      }}
                      disabled={processingConfigId === setting.id}
                      className={`w-full px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                        isAutoPostEnabled(setting)
                          ? "bg-red-600 text-white hover:bg-red-700"
                          : "bg-green-600 text-white hover:bg-green-700"
                      } disabled:opacity-60 disabled:cursor-not-allowed`}
                    >
                      {processingConfigId === setting.id
                        ? "Đang cập nhật..."
                        : isAutoPostEnabled(setting)
                        ? "Tắt thiết lập này"
                        : "Bật thiết lập này"}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <ToastContainer position="top-right" autoClose={3000} />

      {showSettingDetail && selectedSetting && (
        <div
          className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4"
          onClick={closeSettingDetail}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="px-6 py-4 border-b flex justify-between items-start gap-4 sticky top-0 bg-white z-10">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">
                  Chi tiết thiết lập: {getSettingTitle(selectedSetting)}
                </h3>
                <p className="text-xs text-gray-500 mt-1 break-all">
                  Đường dẫn dữ liệu: {selectedSetting.sourcePath}
                </p>
              </div>
              <button
                type="button"
                onClick={closeSettingDetail}
                className="text-gray-500 hover:text-gray-700"
              >
                Đóng
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="flex flex-wrap gap-2">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700">
                  ID: {selectedSetting.id}
                </span>
                <span
                  className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                    isAutoPostEnabled(selectedSetting)
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  {isAutoPostEnabled(selectedSetting) ? "Đang chạy" : "Đã tắt"}
                </span>
                <span
                  className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${sourceMeta.className}`}
                >
                  {selectedSetting.sourceTitle || sourceMeta.label}
                </span>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                  {selectedSetting.platform === "wordpress"
                    ? "WordPress"
                    : "Facebook"}
                </span>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800">
                  {getInputTypeLabel(selectedSetting.inputType)}
                </span>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
                  Fanpage: {selectedFanpageCount}
                </span>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
                  Sản phẩm: {selectedProductsCount}
                </span>
                {selectedSetting.sourceType === "productImages" && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
                    Media: {selectedImagesCount + selectedVideosCount}
                  </span>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {renderSection(
                  "Lịch đăng",
                  <>
                    {renderValueRow(
                      "Kiểu lịch",
                      getScheduleModeLabel(selectedSetting)
                    )}
                    {renderValueRow(
                      "Thời gian đăng",
                      getScheduleLabel(selectedSetting)
                    )}
                    {renderValueRow(
                      "Ngày lặp",
                      getSelectedDayLabels(selectedSetting).join(", ") ||
                        "Không có"
                    )}
                    {Array.isArray(selectedSetting.fixedDateSchedules) &&
                      selectedSetting.fixedDateSchedules.length > 0 && (
                        <div className="text-sm text-gray-700">
                          <span className="font-medium">Lịch cố định:</span>
                          <div className="mt-2 space-y-1">
                            {selectedSetting.fixedDateSchedules.map(
                              (schedule, idx) => (
                                <div
                                  key={`${schedule.date || "date"}-${idx}`}
                                  className="bg-white rounded px-2 py-1 border text-xs"
                                >
                                  {schedule.date || "N/A"} -{" "}
                                  {Array.isArray(schedule.times)
                                    ? schedule.times.join(" | ")
                                    : schedule.time || "Chưa có giờ"}
                                  {schedule.completed ? " - Đã hoàn tất" : ""}
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      )}
                  </>
                )}

                {renderSection(
                  "Thiết lập nội dung",
                  <>
                    {renderValueRow(
                      "Độ dài bài viết",
                      getPostLengthLabel(selectedSetting.postLength)
                    )}
                    {renderValueRow(
                      "Phong cách",
                      selectedSetting.postStyles || "Chưa thiết lập"
                    )}
                    {renderValueRow(
                      "Ngôn ngữ",
                      selectedSetting.selectedLanguage
                        ? getLanguageName(selectedSetting.selectedLanguage)
                        : "Không có"
                    )}
                    {renderValueRow(
                      "Chế độ đăng",
                      getPostModeLabel(selectedSetting.postMode)
                    )}
                    {renderValueRow(
                      "Lặp lại khi hết nội dung",
                      getBooleanLabel(selectedSetting.repeatWhenExhausted === true)
                    )}
                    {renderValueRow(
                      "Chèn thông tin liên hệ",
                      getBooleanLabel(selectedSetting.includeContactInfo === true)
                    )}
                    {renderValueRow(
                      "Dùng thông tin fanpage",
                      getBooleanLabel(selectedSetting.useFanpageInfo === true)
                    )}
                  </>
                )}

                {renderSection(
                  "Ảnh / AI / kiểm duyệt",
                  <>
                    {renderValueRow(
                      "Nguồn ảnh",
                      selectedSetting.imageSource || "Không có"
                    )}
                    {renderValueRow(
                      "Cho phép không dùng ảnh",
                      getBooleanLabel(selectedSetting.allowNoImage === true)
                    )}
                    {renderValueRow(
                      "AI tạo ảnh",
                      getBooleanLabel(
                        selectedSetting.aiImageEnabled === true ||
                          selectedSetting.imageSource === "gemini"
                      )
                    )}
                    {renderValueRow(
                      "Phong cách ảnh",
                      selectedSetting.imageStyle || "Không có"
                    )}
                    {renderValueRow(
                      "Tỉ lệ ảnh",
                      selectedSetting.aspectRatio || "Không có"
                    )}
                    {renderValueRow(
                      "Kiểm duyệt ảnh Internet",
                      selectedSetting.imageValidationEnabled === undefined
                        ? "Không có"
                        : getBooleanLabel(
                            selectedSetting.imageValidationEnabled === true
                          )
                    )}
                    {renderValueRow(
                      "Prompt ảnh riêng",
                      selectedSetting.customImagePrompt || "Không có",
                      { breakWords: true }
                    )}
                  </>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {renderSection(
                  "Kênh đăng",
                  <>
                    {renderValueRow(
                      "Fanpage đã chọn",
                      `${selectedFanpageCount} fanpage`
                    )}
                    {renderFanpageList(selectedSetting)}
                    {selectedSetting.platform === "wordpress" && (
                      <div className="pt-2 border-t space-y-2">
                        {renderValueRow(
                          "Website",
                          selectedSetting.wordpressSiteName ||
                            selectedSetting.websiteUrl ||
                            "Không có"
                        )}
                        {renderValueRow(
                          "Website ID",
                          selectedSetting.wordpressSiteId || "Không có"
                        )}
                        {renderValueRow(
                          "Trạng thái bài WordPress",
                          selectedSetting.wordpressStatus || "Không có"
                        )}
                        {renderValueRow(
                          "Chế độ danh mục",
                          selectedSetting.categoryMode || "Không có"
                        )}
                        {renderValueRow(
                          "Danh mục",
                          Array.isArray(selectedSetting.wordpressCategories)
                            ? selectedSetting.wordpressCategories.join(", ")
                            : selectedSetting.wordpressCategories || "Không có"
                        )}
                        {renderValueRow(
                          "Tags",
                          selectedSetting.wordpressTags || "Không có"
                        )}
                        {renderValueRow(
                          "Cho phép chèn link sản phẩm",
                          getBooleanLabel(
                            selectedSetting.allowProductLinks === true
                          )
                        )}
                      </div>
                    )}
                  </>
                )}

                {renderSection(
                  "Trạng thái vận hành",
                  <>
                    {renderValueRow(
                      "Tạo lúc",
                      formatTimestamp(selectedSetting.createdAt)
                    )}
                    {renderValueRow(
                      "Cập nhật lúc",
                      formatTimestamp(selectedSetting.updatedAt)
                    )}
                    {renderValueRow(
                      "Lần chạy gần nhất",
                      formatTimestamp(selectedSetting.lastRunAt)
                    )}
                    {renderValueRow(
                      "Lần đăng gần nhất",
                      formatTimestamp(selectedSetting.lastPostedAt)
                    )}
                    {renderValueRow(
                      "Sản phẩm vừa đăng",
                      selectedSetting.lastAutoPostedProductName || "Không có"
                    )}
                    {renderValueRow(
                      "Cursor sản phẩm",
                      selectedSetting.productCursor ?? "Không có"
                    )}
                    {renderValueRow(
                      "Hoàn tất toàn bộ sản phẩm",
                      formatTimestamp(selectedSetting.allProductsPostedAt)
                    )}
                    {renderValueRow(
                      "Lỗi sản phẩm gần nhất",
                      selectedSetting.lastProductError || "Không có",
                      { breakWords: true }
                    )}
                  </>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {renderPreBlock("Prompt / Yêu cầu triển khai", selectedSetting.prompt)}
                {renderPreBlock("Thông tin bổ sung", selectedSetting.customInfo)}
              </div>

              <div>
                <h4 className="font-semibold text-gray-800 mb-2">Dữ liệu nguồn</h4>
                <div className="space-y-4 text-sm bg-gray-50 rounded-lg p-4">
                  <div>
                    <div className="font-medium text-gray-700 mb-2">
                      Danh sách chủ đề ({selectedTopicList.length})
                    </div>
                    {selectedTopicList.length > 0 ? (
                      <>
                        <div className="flex flex-wrap gap-2 mb-3">
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                            Tổng: {selectedTopicList.length}
                          </span>
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                            Đã đăng: {postedTopics}
                          </span>
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                            Thất bại: {failedTopics}
                          </span>
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                            Chưa đăng: {pendingTopics}
                          </span>
                        </div>
                        <div className="space-y-2 max-h-72 overflow-auto">
                          {selectedTopicList.map((topic, index) => {
                            const statusMeta = getTopicStatusMeta(
                              topic.postStatus
                            );
                            return (
                              <div
                                key={topic.id || `topic-row-${index}`}
                                className="bg-white rounded border p-3"
                              >
                                <div className="flex items-start justify-between gap-3">
                                  <div className="text-sm text-gray-800 font-medium">
                                    {index + 1}. {topic.title}
                                  </div>
                                  <span
                                    className={`inline-flex shrink-0 items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusMeta.className}`}
                                  >
                                    {statusMeta.label}
                                  </span>
                                </div>
                                {topic.errorMessage && (
                                  <div className="mt-2 text-xs text-red-600">
                                    Lỗi: {topic.errorMessage}
                                  </div>
                                )}
                                {topic.generatedPost && (
                                  <details className="mt-2">
                                    <summary className="text-xs text-blue-600 cursor-pointer">
                                      Xem nội dung đã tạo
                                    </summary>
                                    <pre className="mt-2 bg-gray-50 rounded p-2 border text-xs overflow-x-auto whitespace-pre-wrap">
                                      {topic.generatedPost}
                                    </pre>
                                  </details>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </>
                    ) : (
                      <div className="bg-white rounded p-3 border text-sm text-gray-600">
                        Không có danh sách chủ đề trong thiết lập này.
                      </div>
                    )}
                  </div>

                  <div>
                    <div className="font-medium text-gray-700 mb-2">
                      Danh sách sản phẩm ({selectedProductsCount})
                    </div>
                    {renderProductList(selectedSetting)}
                  </div>

                  {selectedSetting.sourceType === "productImages" && (
                    <div>
                      <div className="font-medium text-gray-700 mb-2">
                        Danh sách ảnh/video ({selectedImagesCount} ảnh,{" "}
                        {selectedVideosCount} video)
                      </div>
                      {renderMediaList(selectedSetting)}
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {renderPreBlock("Links", selectedSetting.links)}
                    {renderPreBlock(
                      "Product posting state",
                      selectedSetting.productPostingState
                    )}
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-gray-800 mb-2">
                  JSON đầy đủ để debug (đã ẩn token/mật khẩu)
                </h4>
                <details className="bg-slate-900 text-slate-100 text-xs rounded">
                  <summary className="px-4 py-3 cursor-pointer select-none">
                    Mở / thu gọn dữ liệu JSON debug
                  </summary>
                  <pre className="px-4 pb-4 overflow-auto max-h-96">
                    {JSON.stringify(sanitizeForDisplay(selectedSetting), null, 2)}
                  </pre>
                </details>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAutoPostSettings;
